import React, { useState, useEffect } from 'react';
import {
    KeyStat,
    CfdOrderRequest,
    CfdSummaryResponse,
    ConciseMarketData
} from '../../api';
import {
    Alert,
    Button,
    Header,
    SpaceBetween,
} from '@cloudscape-design/components';
import {trading212AccountApi, trading212CFDApi} from '../../client/clients';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFlashbar } from '../../context/FlashbarContext';
import { classifyError, alertTypeForKind } from '../../util/errorClassifier';
import { OrderConfirmationModal, OrderFractionLabel } from './OrderConfirmationModal';

// Helper function to format Trading212 API error responses
const formatTradingError = (error: unknown): string => {
    const e = error as {
        response?: { data?: { httpStatusCode?: number; code?: string; apiErrorMessage?: string; message?: string; context?: { type?: string; currency?: string; max?: number } } };
        message?: string;
    };
    if (!e?.response?.data) {
        return e?.message || 'Unknown error occurred';
    }

    const errorData = e.response.data;

    if (errorData.httpStatusCode && (errorData.code || errorData.apiErrorMessage)) {
        let errorMessage = `Trading212 API Error (${errorData.httpStatusCode})`;
        if (errorData.code) errorMessage += `\nError Code: ${errorData.code}`;
        if (errorData.apiErrorMessage) errorMessage += `\nAPI Message: ${errorData.apiErrorMessage}`;
        if (errorData.context) {
            const context = errorData.context;
            errorMessage += `\nDetails:`;
            if (context.type) errorMessage += ` Type: ${context.type}`;
            if (context.currency) errorMessage += ` Currency: ${context.currency}`;
            if (context.max !== undefined) errorMessage += ` Max: ${context.max}`;
        }
        if (errorData.message && errorData.message !== errorData.apiErrorMessage) {
            errorMessage += `\nSystem Message: ${errorData.message}`;
        }
        return errorMessage;
    }

    return errorData.message || e.message || 'Unknown error occurred';
};

interface PendingCfdOrder {
    fraction?: OrderFractionLabel;
    /** Quantity sent as `quantity` to the broker — for CFD this is the GBP-equivalent buy value. */
    orderQuantity: number;
    /** Snapshot of free cash for the post-trade projection inside the modal. */
    freeCash: number;
    /** Latest stock price used for `targetPrice`. */
    currentPrice?: number;
    /** Resolved targetPrice we'll send to the broker. */
    targetPrice: number;
}

export const Trading212CfdPage: React.FC = () => {
    useDocumentTitle('Trading 212 CFD');
    const flashbar = useFlashbar();

    const [ticker, setTicker] = useState<string>('');
    const [keyStats, setKeyStats] = useState<KeyStat[]>([]);
    const [stockPrice, setStockPrice] = useState<ConciseMarketData | null>(null);
    const [cfdSummary, setCfdSummary] = useState<CfdSummaryResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [cfdSummaryLoading, setCfdSummaryLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [cfdError, setCfdError] = useState<string>('');

    const [cfdQuantity, setCfdQuantity] = useState<string>('');
    const [cfdOrderLoading, setCfdOrderLoading] = useState<boolean>(false);
    const [cfdOrderResponse, setCfdOrderResponse] = useState<unknown>(null);
    const [cfdOrderError, setCfdOrderError] = useState<string>('');

    // D1: pending CFD order awaiting confirmation.
    const [pendingOrder, setPendingOrder] = useState<PendingCfdOrder | null>(null);

    const handleFetchStockData = async () => {
        if (!ticker.trim()) {
            setError('Please enter a ticker symbol');
            return;
        }

        setLoading(true);
        setError('');
        setKeyStats([]);
        setStockPrice(null);
        setCfdOrderResponse(null);

        try {
            const [keyStatsResponse, stockPriceResponse] = await Promise.allSettled([
                trading212CFDApi.getKeyStats(ticker.trim().toUpperCase()),
                trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase())
            ]);

            if (keyStatsResponse.status === 'fulfilled') {
                setKeyStats(keyStatsResponse.value.data);
            } else {
                console.error('Error fetching key stats:', keyStatsResponse.reason);
                const classified = classifyError(keyStatsResponse.reason);
                setError((prev) => prev ? `${prev}\n${classified.userMessage}` : classified.userMessage);
            }

            if (stockPriceResponse.status === 'fulfilled') {
                setStockPrice(stockPriceResponse.value.data);
            } else {
                console.error('Error fetching stock price:', stockPriceResponse.reason);
                const classified = classifyError(stockPriceResponse.reason);
                setError((prev) => prev ? `${prev}\n${classified.userMessage}` : classified.userMessage);
            }
        } catch (err: unknown) {
            console.error('Unexpected error:', err);
            const classified = classifyError(err);
            setError(classified.userMessage);
            setKeyStats([]);
            setStockPrice(null);
        } finally {
            setLoading(false);
        }
    };

    const handleFetchCfdSummary = async () => {
        setCfdSummaryLoading(true);
        setCfdError('');

        try {
            const response = await trading212CFDApi.getAccountsSummary();
            setCfdSummary(response.data);
        } catch (err: unknown) {
            console.error('Error fetching CFD summary:', err);
            const classified = classifyError(err, { onRetry: handleFetchCfdSummary });
            setCfdError(classified.userMessage);
            setCfdSummary(null);
        } finally {
            setCfdSummaryLoading(false);
        }
    };

    const handleCfdPlaceOrderRequest = async () => {
        if (!ticker.trim()) {
            setCfdOrderError('Please enter a ticker symbol');
            return;
        }
        if (!cfdQuantity.trim() || isNaN(Number(cfdQuantity)) || Number(cfdQuantity) <= 0) {
            setCfdOrderError('Please enter a valid quantity');
            return;
        }

        setCfdOrderError('');
        setCfdOrderResponse(null);

        try {
            const { data: stockPriceResponse } = await trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase());
            const targetPrice = (stockPriceResponse as ConciseMarketData).close || 0;
            if (targetPrice <= 0) {
                setCfdOrderError('Invalid stock price received');
                return;
            }

            setPendingOrder({
                fraction: 'Custom',
                orderQuantity: Number(cfdQuantity),
                freeCash: cfdSummary?.cash?.free ?? 0,
                currentPrice: targetPrice,
                targetPrice,
            });
        } catch (err: unknown) {
            console.error('Error preparing CFD order:', err);
            setCfdOrderError(`Failed to prepare CFD order: ${formatTradingError(err)}`);
        }
    };

    const handleCfdFractionOrderRequest = async (fraction: number, fractionLabel: OrderFractionLabel) => {
        if (!ticker.trim()) {
            setCfdOrderError('Please enter a ticker symbol');
            return;
        }

        setCfdOrderError('');
        setCfdOrderResponse(null);

        try {
            const { data: settingsResponse } = await trading212CFDApi.getInstrumentSettings(ticker.trim().toUpperCase());

            if (!settingsResponse) {
                setCfdOrderError(`No CFD settings found for ${ticker.trim().toUpperCase()}`);
                return;
            }
            if (!settingsResponse.maxBuyValue || settingsResponse.maxBuyValue < 5) {
                setCfdOrderError(`Max buy value is undefined or too small ${ticker.trim().toUpperCase()}`);
                return;
            }

            const maxBuyAmount = settingsResponse.maxBuyValue * 0.99;
            const orderQuantity = Math.round((maxBuyAmount * fraction) * 100) / 100;

            if (orderQuantity <= 0) {
                setCfdOrderError(`Cannot place ${fractionLabel} CFD order: insufficient max buy amount`);
                return;
            }

            const { data: stockPriceResponse } = await trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase());
            const targetPrice = (stockPriceResponse as ConciseMarketData).close || 0;
            if (targetPrice <= 0) {
                setCfdOrderError('Invalid stock price received');
                return;
            }

            setPendingOrder({
                fraction: fractionLabel,
                orderQuantity,
                freeCash: cfdSummary?.cash?.free ?? 0,
                currentPrice: targetPrice,
                targetPrice,
            });
        } catch (err: unknown) {
            console.error(`Error preparing ${fractionLabel} CFD order:`, err);
            setCfdOrderError(`Failed to prepare ${fractionLabel} CFD order: ${formatTradingError(err)}`);
        }
    };

    /** D1: typed-confirm gate when "All In" or order ≥ 50% of CFD free cash. */
    const requireTypedConfirmFor = (po: PendingCfdOrder): boolean => {
        if (po.fraction === 'All In') return true;
        if (po.freeCash > 0 && po.orderQuantity / po.freeCash >= 0.5) return true;
        // T4-A: also gate when freeCash is zero/negative and order has a real value.
        if (po.freeCash <= 0 && po.orderQuantity > 0) return true;
        return false;
    };

    const handleConfirmedOrder = async () => {
        if (!pendingOrder) return;
        setCfdOrderLoading(true);
        try {
            const orderRequest: CfdOrderRequest = {
                instrumentCode: ticker.trim().toUpperCase(),
                targetPrice: pendingOrder.targetPrice,
                quantity: pendingOrder.orderQuantity,
            };
            const response = await trading212CFDApi.openPosition(orderRequest);
            setCfdOrderResponse(response);
            setCfdQuantity(pendingOrder.orderQuantity.toString());
            flashbar.push({
                type: 'success',
                header: 'CFD position opened',
                content: `${ticker.trim().toUpperCase()} @ ${pendingOrder.targetPrice.toLocaleString()}`,
            });
            setPendingOrder(null);
            handleFetchCfdSummary();
        } catch (err: unknown) {
            console.error('Error placing CFD order:', err);
            const message = formatTradingError(err);
            setCfdOrderError(message);
            flashbar.push({
                type: 'error',
                header: 'CFD order failed',
                content: classifyError(err).userMessage,
            });
        } finally {
            setCfdOrderLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleFetchStockData();
        }
    };

    const formatValue = (value: KeyStat['value']): string => {
        if (!value || value.value === undefined || value.value === null) {
            return 'N/A';
        }

        let displayValue = value.value.toString();

        if (value.currency) {
            displayValue = `${value.currency} ${value.value.toLocaleString()}`;
        } else if (value.type === 'percentage') {
            displayValue = `${value.value}%`;
        } else if (typeof value.value === 'number') {
            displayValue = value.value.toLocaleString();
        }

        return displayValue;
    };

    useEffect(() => {
        handleFetchCfdSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A6: classify the persistent error so the user gets actionable copy.
    const classifiedCfdError = cfdError
        ? classifyError(new Error(cfdError), { onRetry: handleFetchCfdSummary })
        : null;
    const classifiedStockError = error
        ? classifyError(new Error(error), { onRetry: handleFetchStockData })
        : null;

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            {/* D6: persistent leverage warning at the top of the page. */}
            <div style={{ marginBottom: '16px' }}>
                <Alert type="warning" header="Leveraged product">
                    CFDs are complex instruments and come with a high risk of losing money rapidly
                    due to leverage. Losses can exceed your deposit.
                </Alert>
            </div>

            <div style={{ marginBottom: '30px' }}>
                <Header variant="h1">Trading 212 CFD</Header>
            </div>

            {/* Account Overview Section */}
            <div style={{ marginBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <h2 style={{ color: '#333', fontSize: '20px', margin: 0 }}>CFD Account Overview</h2>
                    {/* T4-E: standardized Cloudscape refresh icon button. */}
                    <Button
                        iconName="refresh"
                        variant="icon"
                        ariaLabel="Refresh CFD Account Data"
                        loading={cfdSummaryLoading}
                        onClick={handleFetchCfdSummary}
                    />
                </div>

                {/* A6: classified Alert. */}
                {classifiedCfdError && (
                    <Alert
                        type={alertTypeForKind(classifiedCfdError.kind)}
                        header={classifiedCfdError.userMessage}
                        dismissible
                        onDismiss={() => setCfdError('')}
                        action={
                            classifiedCfdError.suggestedAction ? (
                                <Button
                                    onClick={classifiedCfdError.suggestedAction.onClick}
                                    href={classifiedCfdError.suggestedAction.href}
                                    loading={cfdSummaryLoading}
                                >
                                    {classifiedCfdError.suggestedAction.label}
                                </Button>
                            ) : undefined
                        }
                    />
                )}

                {cfdSummary && cfdSummary.cash ? (
                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '10px 20px',
                            fontWeight: 'bold',
                            textAlign: 'center'
                        }}>
                            CFD Account
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '15px',
                            padding: '20px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Free Cash</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                                    ${cfdSummary.cash.free?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Cash</div>
                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
                                    ${cfdSummary.cash.total?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>P&L</div>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: (cfdSummary.cash.ppl || 0) >= 0 ? '#28a745' : '#dc3545'
                                }}>
                                    ${cfdSummary.cash.ppl?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Margin</div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                    ${cfdSummary.cash.margin?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Result</div>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: (cfdSummary.cash.result || 0) >= 0 ? '#28a745' : '#dc3545'
                                }}>
                                    ${cfdSummary.cash.result?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Indicator</div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                                    ${cfdSummary.cash.indicator?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#666',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px'
                    }}>
                        {cfdSummaryLoading ? 'Loading CFD data...' : 'CFD data not available'}
                    </div>
                )}
            </div>

            {/* Stock Analysis Section */}
            <div>
                <h2 style={{ color: '#333', marginBottom: '15px', fontSize: '20px' }}>Stock Analysis</h2>
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                    Enter a ticker symbol to fetch key statistics and stock price information.
                </p>

                <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginBottom: '30px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <input
                        type="text"
                        placeholder="Enter ticker symbol (e.g., AAPL, TSLA)"
                        value={ticker}
                        onChange={(e) => setTicker(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={loading}
                        style={{
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '16px',
                            minWidth: '250px',
                            flex: '1'
                        }}
                    />
                    <button
                        onClick={handleFetchStockData}
                        disabled={loading || !ticker.trim()}
                        style={{
                            backgroundColor: (loading || !ticker.trim()) ? '#ccc' : '#28a745',
                            color: 'white',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (loading || !ticker.trim()) ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {loading ? 'Fetching...' : 'Get Stock Data'}
                    </button>
                </div>
            </div>

            {/* Place CFD Order Section */}
            <div style={{ marginBottom: '40px' }}>
                <h2 style={{ color: '#333', marginBottom: '15px', fontSize: '20px' }}>Place CFD Order</h2>
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                    Enter a ticker symbol and place CFD orders.
                </p>

                <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ padding: '15px 20px 0 20px' }}>
                        <Header variant="h2">CFD Orders</Header>
                    </div>

                    <div style={{ padding: '20px' }}>
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '20px',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <input
                                type="number"
                                placeholder="Enter quantity"
                                value={cfdQuantity}
                                onChange={(e) => setCfdQuantity(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleCfdPlaceOrderRequest();
                                    }
                                }}
                                disabled={cfdOrderLoading}
                                min="0"
                                step="0.01"
                                style={{
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    flex: '1',
                                    minWidth: '200px'
                                }}
                            />
                            <Button
                                variant="primary"
                                onClick={handleCfdPlaceOrderRequest}
                                disabled={cfdOrderLoading || !ticker.trim() || !cfdQuantity.trim()}
                                loading={cfdOrderLoading}
                            >
                                Place Order
                            </Button>
                        </div>

                        {/* CFD Fraction Order Buttons — D2: All-In demoted to neutral with warning icon. */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ color: '#333', marginBottom: '10px', fontSize: '16px' }}>Fraction Orders</h4>
                            <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                                Place orders for fractions of max buy amount
                            </p>
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="normal"
                                    onClick={() => handleCfdFractionOrderRequest(1 / 5, '1/5')}
                                    disabled={!ticker.trim() || cfdOrderLoading}
                                >
                                    1/5
                                </Button>
                                <Button
                                    variant="normal"
                                    onClick={() => handleCfdFractionOrderRequest(1 / 4, '1/4')}
                                    disabled={!ticker.trim() || cfdOrderLoading}
                                >
                                    1/4
                                </Button>
                                <Button
                                    variant="normal"
                                    onClick={() => handleCfdFractionOrderRequest(1 / 3, '1/3')}
                                    disabled={!ticker.trim() || cfdOrderLoading}
                                >
                                    1/3
                                </Button>
                                <Button
                                    variant="normal"
                                    onClick={() => handleCfdFractionOrderRequest(1 / 2, '1/2')}
                                    disabled={!ticker.trim() || cfdOrderLoading}
                                >
                                    1/2
                                </Button>
                                <Button
                                    variant="normal"
                                    iconAlign="left"
                                    iconName="status-warning"
                                    onClick={() => handleCfdFractionOrderRequest(0.995, 'All In')}
                                    disabled={!ticker.trim() || cfdOrderLoading}
                                >
                                    All In
                                </Button>
                            </SpaceBetween>
                        </div>

                        {cfdOrderError && (
                            <div style={{
                                marginBottom: '10px',
                                padding: '12px',
                                backgroundColor: '#f8d7da',
                                color: '#721c24',
                                border: '1px solid #f5c6cb',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}>
                                <strong>Error:</strong> {cfdOrderError}
                            </div>
                        )}

                        {cfdOrderResponse !== null && (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                                border: '1px solid #c3e6cb',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}>
                                CFD Order placed successfully.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {classifiedStockError && (
                <Alert
                    type={alertTypeForKind(classifiedStockError.kind)}
                    header={classifiedStockError.userMessage}
                    dismissible
                    onDismiss={() => setError('')}
                    action={
                        classifiedStockError.suggestedAction ? (
                            <Button
                                onClick={classifiedStockError.suggestedAction.onClick}
                                href={classifiedStockError.suggestedAction.href}
                                loading={loading}
                            >
                                {classifiedStockError.suggestedAction.label}
                            </Button>
                        ) : undefined
                    }
                />
            )}

            {stockPrice && (
                <div style={{ marginTop: '30px' }}>
                    <h2 style={{ color: '#333', marginBottom: '20px' }}>
                        Stock Price for {ticker.toUpperCase()}
                    </h2>

                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '20px',
                            padding: '20px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Close Price</div>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                                    {stockPrice.close?.toLocaleString() || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Market Session</div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                                    {stockPrice.marketSession || 'N/A'}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Timestamp</div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                    {stockPrice.timestamp || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {keyStats.length > 0 && (
                <div style={{ marginTop: '30px' }}>
                    <h2 style={{ color: '#333', marginBottom: '20px' }}>
                        Key Statistics for {ticker.toUpperCase()} ({keyStats.length} metrics)
                    </h2>

                    <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{
                                        padding: '15px',
                                        textAlign: 'left',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Statistic
                                    </th>
                                    <th style={{
                                        padding: '15px',
                                        textAlign: 'right',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Value
                                    </th>
                                    <th style={{
                                        padding: '15px',
                                        textAlign: 'center',
                                        borderBottom: '1px solid #ddd',
                                        fontWeight: '600',
                                        color: '#333'
                                    }}>
                                        Type
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {keyStats.map((stat, index) => (
                                    <tr
                                        key={`${stat.title}-${index}`}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                    >
                                        <td style={{
                                            padding: '15px',
                                            borderBottom: index === keyStats.length - 1 ? 'none' : '1px solid #eee',
                                            fontWeight: '500',
                                            color: '#333'
                                        }}>
                                            {stat.title}
                                        </td>
                                        <td style={{
                                            padding: '15px',
                                            textAlign: 'right',
                                            borderBottom: index === keyStats.length - 1 ? 'none' : '1px solid #eee',
                                            fontWeight: '600',
                                            color: stat.value?.value !== undefined ? '#28a745' : '#6c757d'
                                        }}>
                                            {formatValue(stat.value)}
                                        </td>
                                        <td style={{
                                            padding: '15px',
                                            textAlign: 'center',
                                            borderBottom: index === keyStats.length - 1 ? 'none' : '1px solid #eee',
                                            fontSize: '12px',
                                            color: '#666'
                                        }}>
                                            {stat.value?.type || 'N/A'}
                                            {stat.value?.compact && (
                                                <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                                                    Compact
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && !error && keyStats.length === 0 && ticker && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#d1ecf1',
                    color: '#0c5460',
                    border: '1px solid #bee5eb',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    No key statistics found for ticker "{ticker.toUpperCase()}".
                    Please verify the ticker symbol and ensure Trading212 credentials are configured.
                </div>
            )}

            {!loading && !error && keyStats.length === 0 && !ticker && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    Enter a ticker symbol above to fetch key statistics from Trading212.
                </div>
            )}

            {/* D1: confirmation modal in front of every CFD order. */}
            {pendingOrder && (
                <OrderConfirmationModal
                    account="CFD"
                    ticker={ticker}
                    currentPrice={pendingOrder.currentPrice}
                    freeCash={pendingOrder.freeCash}
                    orderValue={pendingOrder.orderQuantity}
                    fraction={pendingOrder.fraction}
                    requireTypedConfirm={requireTypedConfirmFor(pendingOrder)}
                    loading={cfdOrderLoading}
                    onConfirm={handleConfirmedOrder}
                    onDismiss={() => {
                        if (cfdOrderLoading) return;
                        setPendingOrder(null);
                    }}
                />
            )}
        </div>
    );
};
